<template>
    <!-- 隐藏的file input -->
    <input ref="fileInput" type="file" accept="image/*" style="display: none" @change="handleFileSelect" />

    <!-- 触发按钮 -->
    <slot :openFilePicker="triggerFileInput" :uploading="uploading">
        <div class="small-icon-button" @click="triggerFileInput">
            <ImageIcon />
        </div>
    </slot>
</template>

<!-- eslint-disable @typescript-eslint/no-explicit-any -->
<script setup lang="ts">
import { ref } from 'vue'
import ImageIcon from "../icon/ImageIcon.vue";

// 定义props
interface Props {
    uploadUrl: string // 上传API地址
    maxSize?: number // 最大文件大小，单位MB
    additionalData?: Record<string, any> // 额外参数
    headers?: Record<string, string> // 请求头
    accept?: string // 接受的文件类型
}

const props = withDefaults(defineProps<Props>(), {
    maxSize: 5,
    accept: 'image/*'
})

// 定义事件
const emit = defineEmits<{
    'upload:start': []
    'upload:success': [response: any]
    'upload:error': [error: Error]
    'upload:progress': [progress: number]
}>()

// 响应式数据
const fileInput = ref<HTMLInputElement>()
const uploading = ref(false)
const uploadProgress = ref(0)

// 触发文件选择
const triggerFileInput = () => {
    fileInput.value?.click()
}

// 处理文件选择
const handleFileSelect = async (event: Event) => {
    const target = event.target as HTMLInputElement
    const file = target.files?.[0]

    if (file) {
        // 验证文件
        if (!validateFile(file)) {
            target.value = '' // 重置选择
            return
        }

        // 执行上传
        try {
            const result = await uploadFile(file)
            emit('upload:success', result)
        } catch (error) {
            emit('upload:error', error as Error)
        }
    }

    // 重置input
    target.value = ''
}

// 验证文件
const validateFile = (file: File): boolean => {
    // 验证文件类型
    if (!file.type.startsWith('image/')) {
        alert('请选择图片文件')
        return false
    }

    // 验证文件大小
    const maxSizeInBytes = props.maxSize * 1024 * 1024
    if (file.size > maxSizeInBytes) {
        alert(`文件大小不能超过 ${props.maxSize}MB`)
        return false
    }

    return true
}

// 上传文件
const uploadFile = async (file: File) => {
    uploading.value = true
    uploadProgress.value = 0

    try {
        emit('upload:start')

        const formData = new FormData()
        formData.append('file', file)

        // 添加额外的表单数据
        if (props.additionalData) {
            Object.keys(props.additionalData).forEach(key => {
                formData.append(key, props.additionalData![key])
            })
        }

        return await new Promise((resolve, reject) => {
            const xhr = new XMLHttpRequest()

            // 监听上传进度
            xhr.upload.addEventListener('progress', (event) => {
                if (event.lengthComputable) {
                    const progress = Math.round((event.loaded / event.total) * 100)
                    uploadProgress.value = progress
                    emit('upload:progress', progress)
                }
            })

            xhr.addEventListener('load', () => {
                if (xhr.status >= 200 && xhr.status < 300) {
                    try {
                        const responseData = JSON.parse(xhr.responseText)
                        resolve(responseData)
                    } catch {
                        resolve(xhr.responseText)
                    }
                } else {
                    reject(new Error(`上传失败: ${xhr.statusText}`))
                }
            })

            xhr.addEventListener('error', () => {
                reject(new Error('上传过程中发生网络错误'))
            })

            xhr.open('POST', props.uploadUrl)

            // 设置请求头
            if (props.headers) {
                Object.keys(props.headers).forEach(key => {
                    xhr.setRequestHeader(key, props.headers![key])
                })
            }

            xhr.send(formData)
        })

    } catch (error) {
        throw error
    } finally {
        uploading.value = false
        uploadProgress.value = 0
    }
}

// 暴露方法
defineExpose({
    triggerFileInput,
    uploadFile
})
</script>

<style lang="less" scoped>
.small-icon-button {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 16px;
    height: 16px;
    cursor: pointer;

    &:hover {
        scale: 1.05;
    }
}
</style>
